import { Link } from 'react-router-dom';
import {
  Anchor,
  Box,
  Card,
  CardBody,
  Heading,
  Page,
  PageContent,
  Paragraph,
  Text,
} from 'grommet';
import { Alert, Trophy } from 'grommet-icons';

import { allSeasons, HALL_OF_SHAME, RECORD_BOOK } from '../seasons';
import { findDoc } from '../docs/manifest';

// A record-book style row: title + holder on the left, big number right.
const StatRow = ({ title, holder, detail, value, color = 'brand', border = true }) => (
  <Box
    direction="row"
    align="baseline"
    justify="between"
    gap="small"
    pad={{ vertical: 'small' }}
    border={border ? { side: 'bottom', color: 'light-3' } : undefined}
  >
    <Box>
      <Text size="small" weight="bold" color="dark-1">{title}</Text>
      <Text size="xsmall" color="dark-2">
        {holder ? `${holder} · ${detail}` : detail}
      </Text>
    </Box>
    <Text size="large" weight="bold" color={value ? color : 'dark-2'} style={{ whiteSpace: 'nowrap' }}>
      {value ?? 'TBD'}
    </Text>
  </Box>
);

export default function History() {
  // Seasons with a results block, newest first. The in-progress season
  // (results: null) stays off this page until it's decided.
  const finished = allSeasons().filter((s) => s.results);
  const firstOuts = finished.filter((s) => s.results.firstOut);

  return (
    <Page kind="narrow" pad="medium">
      <PageContent gap="medium">
        <Box flex={false}>
          <Heading level={1} margin="none">League History</Heading>
          <Paragraph margin="none" color="dark-2">
            {finished.length} seasons of chaos, on the record.
          </Paragraph>
        </Box>

        <Card flex={false}>
          <CardBody pad="medium" gap="small">
            <Box direction="row" align="center" gap="xsmall">
              <Trophy size="20px" color="status-warning" />
              <Heading level={3} margin="none">Champions</Heading>
            </Box>
            <Box>
              {finished.map((s, i) => (
                <Box
                  key={s.season}
                  direction="row"
                  align="center"
                  gap="small"
                  pad={{ vertical: 'small' }}
                  border={i < finished.length - 1 ? { side: 'bottom', color: 'light-3' } : undefined}
                >
                  <Box background="brand" round="xsmall" pad={{ horizontal: 'xsmall', vertical: 'xxsmall' }}>
                    <Text size="small" weight="bold">{s.season}</Text>
                  </Box>
                  <Box>
                    <Text weight="bold" color="dark-1">
                      {s.results.champion ? s.results.champion.team : 'TBD'}
                    </Text>
                    {s.results.champion?.note && (
                      <Text size="xsmall" color="dark-2">{s.results.champion.note}</Text>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </CardBody>
        </Card>

        <Card flex={false}>
          <CardBody pad="medium" gap="xsmall">
            <Heading level={3} margin="none">Record book</Heading>
            <Box>
              {RECORD_BOOK.map((r, i) => (
                <StatRow key={r.title} {...r} border={i < RECORD_BOOK.length - 1} />
              ))}
            </Box>
          </CardBody>
        </Card>

        <Card flex={false}>
          <CardBody pad="medium" gap="small">
            <Box direction="row" align="center" gap="xsmall">
              <Alert size="20px" color="status-critical" />
              <Heading level={3} margin="none">Hall of Shame</Heading>
            </Box>
            <Text size="small" weight="bold" color="dark-2">First out of the gate</Text>
            <Box>
              {firstOuts.map((s, i) => (
                <Box
                  key={s.season}
                  direction="row"
                  align="center"
                  justify="between"
                  gap="small"
                  pad={{ vertical: 'xsmall' }}
                  border={i < firstOuts.length - 1 ? { side: 'bottom', color: 'light-3' } : undefined}
                >
                  <Text size="small">
                    <Text size="small" color="dark-2">{s.season} · </Text>
                    <Text size="small" weight="bold" color="dark-1">{s.results.firstOut.team}</Text>
                  </Text>
                  <Text size="small" weight="bold" color="status-critical" style={{ whiteSpace: 'nowrap' }}>
                    Week {s.results.firstOut.week}
                  </Text>
                </Box>
              ))}
            </Box>
            <Box border={{ side: 'top', color: 'light-3' }}>
              {HALL_OF_SHAME.map((r, i) => (
                <StatRow
                  key={r.title}
                  {...r}
                  color="status-critical"
                  border={i < HALL_OF_SHAME.length - 1}
                />
              ))}
            </Box>
          </CardBody>
        </Card>

        <Card background="light-2" elevation="none" flex={false}>
          <CardBody pad="medium" gap="xsmall">
            <Text size="small" weight="bold" color="dark-1">Dig deeper</Text>
            <Box direction="row" gap="medium" wrap>
              {finished.map((s) => {
                const doc = findDoc(`${s.season}-season`);
                return doc ? (
                  <Anchor key={s.season} as={Link} to={`/docs/${doc.slug}`} weight="bold" size="small">
                    {s.season} archive
                  </Anchor>
                ) : null;
              })}
            </Box>
          </CardBody>
        </Card>
      </PageContent>
    </Page>
  );
}
